import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { updateMenuSchema, type UpdateMenuFormValues } from '../schema/menuSchema';
import type { MenuDetailResponse, MenuTreeNode } from '../types/menu';
import { SystemMenuBadge } from './SystemMenuBadge';
import { usePermissionCatalog } from '../hooks/usePermissionCatalog';

interface MenuFormProps {
  menu: MenuDetailResponse;
  treeNodes: MenuTreeNode[];
  onSave: (values: UpdateMenuFormValues) => void;
  onDelete: () => void;
  isSaving: boolean;
  saveError?: string;
  /** EXEC 샌드박스 모드: system_menu=true 행의 [수정]/[삭제] 버튼 disabled */
  isExecSandbox?: boolean;
}


function flattenTree(nodes: MenuTreeNode[], excludeId?: number): Array<{ id: number; label: string; depth: number }> {
  const result: Array<{ id: number; label: string; depth: number }> = [];
  function walk(list: MenuTreeNode[], d: number) {
    for (const n of list) {
      if (n.id !== excludeId) {
        result.push({ id: n.id, label: n.nameKo, depth: d });
        if (n.children) walk(n.children, d + 1);
      }
    }
  }
  walk(nodes, 0);
  return result;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  boxSizing: 'border-box',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: 'var(--color-danger)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  display: 'block',
  marginBottom: 'var(--spacing-1)',
};

const errorMsgStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 2,
};

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  marginBottom: 'var(--spacing-4)',
};

/**
 * MenuForm — 선택된 메뉴의 수정 폼.
 * 시스템 메뉴(systemMenu=true)의 url/parentId/requiredRole은 disabled 처리.
 */
export function MenuForm({ menu, treeNodes, onSave, onDelete, isSaving, saveError, isExecSandbox = false }: MenuFormProps) {
  const { t } = useTranslation();
  // EXEC 샌드박스 모드에서 system_menu=true 행은 mutation 불가
  const isSystemLocked = isExecSandbox && menu.systemMenu;

  // #471 — 권한 카탈로그 (필요 권한 드롭다운)
  const { data: permissionCatalog = [] } = usePermissionCatalog();
  const REQUIRED_ROLES = [
    { value: '', label: t('menu.form.field.requiredRole.placeholder') },
    { value: 'ROLE_ADMIN', label: 'ROLE_ADMIN' },
    { value: 'ROLE_MANAGER', label: 'ROLE_MANAGER' },
    { value: 'ROLE_USER', label: 'ROLE_USER' },
    { value: 'ROLE_GUEST', label: 'ROLE_GUEST' },
  ];
  const MENU_TYPES = [
    { value: 'INTERNAL', label: t('menu.form.field.menuType.INTERNAL') },
    { value: 'EXTERNAL', label: t('menu.form.field.menuType.EXTERNAL') },
    { value: 'DIVIDER', label: t('menu.form.field.menuType.DIVIDER') },
  ];
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<UpdateMenuFormValues>({
    resolver: zodResolver(updateMenuSchema),
    defaultValues: {
      nameKo: menu.nameKo ?? '',
      nameEn: menu.nameEn ?? '',
      labelKey: menu.labelKey ?? '',
      url: menu.url ?? '',
      icon: menu.icon ?? '',
      menuType: menu.menuType,
      parentId: menu.parentId ?? null,
      sortOrder: menu.sortOrder,
      visible: menu.visible,
      requiredRole: menu.requiredRole ?? '',
      requiredPermission: menu.requiredPermission ?? null,
      description: menu.description ?? '',
    },
  });

  // Reset form when selected menu changes
  useEffect(() => {
    reset({
      nameKo: menu.nameKo ?? '',
      nameEn: menu.nameEn ?? '',
      labelKey: menu.labelKey ?? '',
      url: menu.url ?? '',
      icon: menu.icon ?? '',
      menuType: menu.menuType,
      parentId: menu.parentId ?? null,
      sortOrder: menu.sortOrder,
      visible: menu.visible,
      requiredRole: menu.requiredRole ?? '',
      requiredPermission: menu.requiredPermission ?? null,
      description: menu.description ?? '',
    });
  }, [menu.id, reset]);

  const flatParents = flattenTree(treeNodes, menu.id);
  const isSystemMenu = menu.systemMenu;

  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: isSaving ? 'not-allowed' : 'pointer',
    opacity: isSaving ? 0.7 : 1,
    fontFamily: 'var(--font-family-sans)',
  };

  const btnDanger: React.CSSProperties = {
    ...btnPrimary,
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-danger)',
    color: 'var(--color-danger)',
    cursor: 'pointer',
    opacity: 1,
  };

  return (
    <form
      onSubmit={handleSubmit(onSave)}
      aria-label={t('menu.form.title.edit')}
      noValidate
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-4)',
          paddingBottom: 'var(--spacing-4)',
          borderBottom: '1px solid var(--color-surface-border)',
          flexWrap: 'wrap',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {menu.nameKo}
        </h2>
        <SystemMenuBadge systemMenu={isSystemMenu} />
        {menu.childCount != null && menu.childCount > 0 && (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
            }}
          >
            {menu.childCount}
          </span>
        )}
      </div>

      {/* System menu notice */}
      {isSystemMenu && (
        <div
          role="note"
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-warning) 10%, var(--color-surface-muted))',
            border: '1px solid var(--color-warning)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-warning)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {t('menu.form.systemMenu.hint')}
        </div>
      )}

      {/* Save error */}
      {saveError && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-danger) 8%, var(--color-surface-muted))',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-danger)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {saveError}
        </div>
      )}

      {/* Fields */}
      <div style={fieldStyle}>
        <label htmlFor="field-nameKo" style={labelStyle}>
          {t('menu.form.field.nameKo')} *
        </label>
        <input
          id="field-nameKo"
          type="text"
          {...register('nameKo')}
          aria-invalid={!!errors.nameKo}
          aria-describedby={errors.nameKo ? 'err-nameKo' : undefined}
          style={errors.nameKo ? inputErrorStyle : inputStyle}
          placeholder={t('menu.form.field.nameKo.placeholder')}
        />
        {errors.nameKo && (
          <span id="err-nameKo" role="alert" style={errorMsgStyle}>
            {errors.nameKo.message}
          </span>
        )}
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-nameEn" style={labelStyle}>
          {t('menu.form.field.nameEn')}
        </label>
        <input
          id="field-nameEn"
          type="text"
          {...register('nameEn')}
          style={inputStyle}
          placeholder="English menu name"
        />
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-menuType" style={labelStyle}>
          {t('menu.form.field.menuType')}
        </label>
        <select
          id="field-menuType"
          {...register('menuType')}
          aria-invalid={!!errors.menuType}
          style={errors.menuType ? inputErrorStyle : inputStyle}
        >
          {MENU_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        {errors.menuType && (
          <span role="alert" style={errorMsgStyle}>
            {errors.menuType.message}
          </span>
        )}
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-url" style={labelStyle}>
          URL
        </label>
        <input
          id="field-url"
          type="text"
          {...register('url')}
          disabled={isSystemMenu}
          aria-disabled={isSystemMenu}
          aria-describedby={isSystemMenu ? 'system-url-hint' : undefined}
          style={{
            ...inputStyle,
            opacity: isSystemMenu ? 0.55 : 1,
            cursor: isSystemMenu ? 'not-allowed' : 'text',
          }}
          placeholder={t('menu.form.field.url.placeholder')}
        />
        {isSystemMenu && (
          <span id="system-url-hint" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            {t('menu.form.systemMenu.hint')}
          </span>
        )}
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-icon" style={labelStyle}>
          {t('menu.form.field.icon')}
        </label>
        <input
          id="field-icon"
          type="text"
          {...register('icon')}
          style={inputStyle}
          placeholder={t('menu.form.field.icon.placeholder')}
        />
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-parent" style={labelStyle}>
          {t('menu.form.field.parent')}
        </label>
        <select
          id="field-parent"
          disabled={isSystemMenu}
          aria-disabled={isSystemMenu}
          style={{
            ...inputStyle,
            opacity: isSystemMenu ? 0.55 : 1,
            cursor: isSystemMenu ? 'not-allowed' : 'auto',
          }}
          defaultValue={menu.parentId ?? ''}
          {...(!isSystemMenu ? register('parentId', { setValueAs: (v) => (v === '' ? null : Number(v)) }) : {})}
        >
          <option value="">{t('menu.form.field.parent.root')}</option>
          {flatParents.map((p) => (
            <option key={p.id} value={p.id}>
              {'  '.repeat(p.depth)}{p.label}
            </option>
          ))}
        </select>
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-sortOrder" style={labelStyle}>
          {t('menu.form.field.sortOrder')}
        </label>
        <input
          id="field-sortOrder"
          type="number"
          min={0}
          max={99999}
          {...register('sortOrder', { valueAsNumber: true })}
          aria-invalid={!!errors.sortOrder}
          style={errors.sortOrder ? inputErrorStyle : inputStyle}
          placeholder="10"
        />
        {errors.sortOrder && (
          <span role="alert" style={errorMsgStyle}>
            {errors.sortOrder.message}
          </span>
        )}
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-visible" style={labelStyle}>
          {t('menu.form.field.visible')}
        </label>
        <select
          id="field-visible"
          style={inputStyle}
          {...register('visible', { setValueAs: (v) => v === 'true' })}
          defaultValue={menu.visible ? 'true' : 'false'}
        >
          <option value="true">{t('menu.form.field.visible.true')}</option>
          <option value="false">{t('menu.form.field.visible.false')}</option>
        </select>
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-requiredRole" style={labelStyle}>
          {t('menu.form.field.requiredRole')}
        </label>
        <select
          id="field-requiredRole"
          disabled={isSystemMenu}
          aria-disabled={isSystemMenu}
          style={{
            ...inputStyle,
            opacity: isSystemMenu ? 0.55 : 1,
            cursor: isSystemMenu ? 'not-allowed' : 'auto',
          }}
          {...(!isSystemMenu ? register('requiredRole') : {})}
          defaultValue={menu.requiredRole ?? ''}
        >
          {REQUIRED_ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      {/* #471 — 필요 권한 (requiredPermission) */}
      <div style={fieldStyle}>
        <label htmlFor="field-requiredPermission" style={labelStyle}>
          {t('menu.form.field.requiredPermission')}
        </label>
        <select
          id="field-requiredPermission"
          disabled={isSystemMenu}
          aria-disabled={isSystemMenu}
          aria-describedby="hint-requiredPermission"
          style={{
            ...inputStyle,
            opacity: isSystemMenu ? 0.55 : 1,
            cursor: isSystemMenu ? 'not-allowed' : 'auto',
          }}
          {...(!isSystemMenu
            ? register('requiredPermission', {
                setValueAs: (v: string) => (v === '' ? null : v),
              })
            : {})}
          defaultValue={menu.requiredPermission ?? ''}
        >
          <option value="">{t('menu.form.field.requiredPermission.none')}</option>
          {permissionCatalog.map((p) => (
            <option key={p.code} value={p.code}>
              {p.nameKo} ({p.code})
            </option>
          ))}
        </select>
        <span
          id="hint-requiredPermission"
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            marginTop: 'var(--spacing-1)',
          }}
        >
          {t('menu.form.field.requiredPermission.helper')}
        </span>
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-labelKey" style={labelStyle}>
          {t('menu.form.field.labelKey')}
        </label>
        <input
          id="field-labelKey"
          type="text"
          {...register('labelKey')}
          style={inputStyle}
          placeholder={t('menu.form.field.labelKey.placeholder')}
        />
      </div>

      <div style={fieldStyle}>
        <label htmlFor="field-description" style={labelStyle}>
          {t('menu.form.field.description')}
        </label>
        <textarea
          id="field-description"
          rows={2}
          {...register('description')}
          style={{
            ...inputStyle,
            resize: 'vertical',
            minHeight: 60,
          }}
          placeholder={t('menu.form.field.description.placeholder')}
        />
      </div>

      {/* Actions */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 'var(--spacing-2)',
        }}
      >
        <button
          type="button"
          disabled={isSystemLocked}
          onClick={isSystemLocked ? undefined : onDelete}
          title={isSystemLocked ? t('menuMgmt.sandbox.exec.lockedTooltip') : undefined}
          style={{
            ...btnDanger,
            opacity: isSystemLocked ? 0.45 : 1,
            cursor: isSystemLocked ? 'not-allowed' : 'pointer',
          }}
          aria-label={`${menu.nameKo} ${t('menu.action.delete')}`}
          aria-disabled={isSystemLocked}
        >
          {t('menu.action.delete')}
        </button>
        <button
          type="submit"
          disabled={isSaving || !isDirty || isSystemLocked}
          title={isSystemLocked ? t('menuMgmt.sandbox.exec.lockedTooltip') : undefined}
          style={{
            ...btnPrimary,
            opacity: isSaving || !isDirty || isSystemLocked ? 0.6 : 1,
            cursor: isSaving || !isDirty || isSystemLocked ? 'not-allowed' : 'pointer',
          }}
          aria-busy={isSaving}
          aria-disabled={isSystemLocked}
        >
          {isSaving ? t('menu.form.button.loading.save') : t('menu.form.button.save')}
        </button>
      </div>
    </form>
  );
}
